import { useCallback } from 'react';

import { GroupedOption, SelectOption } from '~/shared/utils/types';
import { useXFetch } from '~/shared/utils/useXFetch';
import { VolumesAutoCompleteResponse } from '~/shared/types/api';

import { useSearchFilter } from './common/useSearchFilter';
import SearchFilterBase from './common/SearchFilterBase';
import SearchFilterOptionRow from './common/SearchFilterOptionRow';

export default function SearchVolumeTags({
  filters,
  setFilters,
  isDisclosureOpen,
  accounts,
}: {
  filters: SelectOption[];
  setFilters: (selections: SelectOption[]) => void;
  isDisclosureOpen?: boolean;
  accounts?: string;
}) {
  const api = useXFetch();

  const searchOptions = useCallback(
    async (pattern: string): Promise<GroupedOption[]> => {
      const trimmedPattern = pattern.trim();

      if (!trimmedPattern) {
        return [];
      }

      try {
        // Parse tag pattern: "key:value" or just "key"
      let key: string;
      let val: string;
      
      if (!trimmedPattern.includes(':')) {
        // No colon - treat entire input as key
        key = trimmedPattern;
        val = '';
      } else {
        // Use last colon as delimiter between key and value
        const lastColonIndex = trimmedPattern.lastIndexOf(':');
        key = trimmedPattern.substring(0, lastColonIndex).trim();
        val = trimmedPattern.substring(lastColonIndex + 1).trim();
      }
        if (!key) {
          return [];
        }

        const params = new URLSearchParams({ field: `tag:${key}`, pattern: val });
        if (accounts) params.set('accounts', accounts);
        const qs = params.toString();

        const response = await api<VolumesAutoCompleteResponse>(
          `/api/v1/volumes/autocomplete?${qs}`,
          { cache: 'no-store' }
        );
        const values = response.values || [];

        const groupedResults: GroupedOption[] =
          values.length > 0
            ? [
                {
                  label: 'Tags',
                  options: values.map((v) => ({
                    id: key && v ? `${key}:${v}` : key || v,
                    name: key && v ? `${key}:${v}` : key || v,
                    group: `tag:${key}` as const,
                  })),
                },
              ]
            : [];

        return groupedResults;
      } catch (error) {
        console.error('[VolumeTagsFilter] Error searching tags:', error);
        return [];
      }
    },
    [api, accounts]
  );

  const {
    searchResults,
    isLoading,
    inputValue,
    showResults,
    focusedIndex,
    localSelections,
    selectedIdSet,
    flattenedResults,
    inputRef,
    resultsRef,
    handleInputChange,
    handleInputFocus,
    handleInputBlur,
    handleSelect,
    handleResultsMouseDown,
    handleResultsMouseUp,
    handleKeyDown,
  } = useSearchFilter({
    initialSelections: filters,
    onSelectionsChange: setFilters,
    searchFunction: searchOptions,
    filterGroup: 'tags',
    debounceMs: 200,
    defaultQuery: 'Name: ',
    isDisclosureOpen,
  });

  const renderOption = useCallback(
    (option: SelectOption, isSelected: boolean, isFocused: boolean = false) => (
      <SearchFilterOptionRow
        option={option}
        isSelected={isSelected}
        onSelect={handleSelect}
        tagVariant="volume"
        isFocused={isFocused}
      />
    ),
    [handleSelect]
  );

  return (
    <div className="flex w-full flex-1 flex-col">
      <SearchFilterBase
        inputValue={inputValue}
        onInputChange={handleInputChange}
        inputRef={inputRef}
        isDisclosureOpen={isDisclosureOpen}
        placeholder="Search"
        searchResults={searchResults}
        isLoading={isLoading}
        showResults={showResults}
        focusedIndex={focusedIndex}
        flattenedResults={flattenedResults}
        localSelections={localSelections}
        onSelect={handleSelect}
        selectedIdSet={selectedIdSet}
        renderOption={renderOption}
        className="w-full"
        mustTypeFirstMessage="Type a tag as key:value"
        resultsRef={resultsRef}
        onResultsMouseDown={handleResultsMouseDown}
        onResultsMouseUp={handleResultsMouseUp}
        onKeyDown={handleKeyDown}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
      />
    </div>
  );
}
